import React, { useEffect, useMemo, useState } from 'react';
import CustomModal from 'shared/components/andtComponents/Modal';
import Stepper from 'shared/components/andtComponents/Stepper/Stepper';
import { GenerateIcon, IconNames, ICONS } from '@pileus-cloud/anodot-frontend-common';
import toast from 'shared/components/andtComponents/Toast';
import styles from './EditAccountCurrencyModal.module.scss';
import { ApprovalStep } from './ApprovalStep';
import { CurrencySelectionStep } from './CurrencySelectionStep';
import Account from 'users/models/account';
import UsersStore from 'users/store/usersStore';
import { ViewTypes } from 'shared/components/CurrencySelect/processingCurrencyViewType.enum';
import { CurrencyContext } from 'app/contexts/Currency/CurrencyContext';
import { Currency } from 'shared/components/CurrencySelect/types';
import { getViewType } from 'shared/components/CurrencySelect/Currency.utils';

interface EditProcessingCurrencyModalProps {
  account: Account;
  currencyContext: CurrencyContext;
  usersStore: UsersStore;
  onClose: () => void;
  isOpen: boolean;
  editProcessingCurrencyHandler: (currency: Currency | null) => Promise<void>;
}

const getModelSteps = (params: {
  newProcessingCurrency: Currency | null;
  setNewProcessingCurrency: (currency: Currency) => void;
  viewType: ViewTypes;
  processingCurrency: Currency;
  currenciesArray: Currency[];
  providerCurrency: Currency;
  usersStore: UsersStore;
}) => {
  const {
    newProcessingCurrency,
    setNewProcessingCurrency,
    viewType,
    processingCurrency,
    currenciesArray,
    providerCurrency,
    usersStore,
  } = params;
  return [
    {
      id: 'edit',
      saveButtonTitle: 'Next',
      title: 'Currency Selection',
      Component: () => (
        <CurrencySelectionStep
          newProcessingCurrency={newProcessingCurrency}
          setNewProcessingCurrency={setNewProcessingCurrency}
          viewType={viewType}
          processingCurrency={processingCurrency}
          currenciesArray={currenciesArray}
        />
      ),
    },
    {
      id: 'summary',
      saveButtonTitle: 'Apply New Currency',
      title: 'Summary',
      Component: () => (
        <ApprovalStep
          providerCurrency={providerCurrency}
          isMsp={usersStore.isCurrentUserMSP}
          newProcessingCurrency={newProcessingCurrency!}
        />
      ),
    },
  ];
};

const EditProcessingCurrencyModal: React.FC<EditProcessingCurrencyModalProps> = ({
  account,
  currencyContext,
  usersStore,
  onClose,
  isOpen,
  editProcessingCurrencyHandler,
}) => {
  const { currenciesArray, currenciesMap } = currencyContext;
  const [newProcessingCurrency, setNewProcessingCurrency] = useState<Currency | null>(null);
  const [screenIndex, setScreenIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(!newProcessingCurrency || isSaving);

  const providerCurrency = currenciesMap[account.providerCurrency];
  const processingCurrency = currenciesMap[account.processingCurrency];

  useEffect(() => {
    if (processingCurrency && newProcessingCurrency?.code === processingCurrency?.code) {
      setIsSaveDisabled(true);
    } else {
      setIsSaveDisabled(!newProcessingCurrency || isSaving);
    }
  }, [newProcessingCurrency, processingCurrency, isSaving]);

  const modelSteps = useMemo(() => {
    const viewType = getViewType(account.cloudTypeId, account.accountTypeId, account.isM3c);

    return getModelSteps({
      newProcessingCurrency,
      setNewProcessingCurrency,
      viewType,
      processingCurrency,
      currenciesArray,
      providerCurrency,
      usersStore,
    });
  }, [
    providerCurrency,
    currenciesMap,
    newProcessingCurrency,
    processingCurrency,
    usersStore.isCurrentUserMSP,
    currenciesArray,
  ]);

  const Page = modelSteps?.[screenIndex]?.Component;

  const setNextPage = async () => {
    try {
      if (screenIndex < modelSteps.length - 1) {
        const nextScreenIndex = screenIndex + 1;
        setScreenIndex(nextScreenIndex);
      } else {
        await editProcessingCurrencyHandler(newProcessingCurrency);
        onClose();
      }
    } catch (error: any) {
      if (error.response?.status === 500) {
        toast.error(`Error on the ${modelSteps?.[screenIndex].title} step`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const getButtonTitle = (text: string, icon: string | null, isLeft: boolean) => (
    <div className={styles.buttonTitle}>
      {icon && isLeft && <GenerateIcon iconName={icon as IconNames} className={styles.buttonIcon} />}
      <div>{text}</div>
      {icon && !isLeft && <GenerateIcon iconName={icon as IconNames} className={styles.buttonIcon} />}
    </div>
  );

  const getSaveButtonTitle = () => {
    return modelSteps?.[screenIndex].saveButtonTitle;
  };

  return (
    <CustomModal
      automationid="edit-modal"
      cancelTitle="Cancel"
      className={styles}
      closeOnSave={false}
      isSecondPrimaryError={false}
      onClose={onClose}
      headerMode="edit"
      onSave={setNextPage}
      open={isOpen}
      onSecondPrimaryClick={() => setScreenIndex(screenIndex - 1)}
      secondPrimaryTitle={screenIndex !== 0 ? getButtonTitle('Back', ICONS.chevronLeft.name, true) : null}
      secondPrimaryDisabled={screenIndex === 0 || isSaving}
      saveDisabled={isSaveDisabled}
      saveTitle={getButtonTitle(
        getSaveButtonTitle(),
        screenIndex < modelSteps.length - 1 ? ICONS.chevronRight.name : null,
        false,
      )}
      title={`Set the processing currency for - ${account.accountName}`}
    >
      <div className={styles.stepperContainer}>
        <Stepper activeStepIndex={screenIndex} steps={modelSteps} />
      </div>
      {Page && <Page />}
    </CustomModal>
  );
};

export default EditProcessingCurrencyModal;
